/**
 * React Query hooks for the import-jobs admin surface.
 *
 * Wraps the admin REST endpoints and surfaces "what monitors do I have"
 * + "what runs has each fired" so the Monitor runs page can render run
 * history + error reasons inline. The prompt-agent admin (useScheduledJobs)
 * has the same shape but filters at the backend to JobType.PROMPT_AGENT;
 * this hook hits the parallel import-jobs routes.
 *
 * Polling cadence mirrors useScheduledJobs:
 *  - list: 10s while ANY job has a pending/running latest run
 *  - run list: 5s while any run is pending/running
 *  - run detail: 2s while the run is running
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

import { fetchWithAuth } from '../utils/api';
import type {
  ImportJob,
  ImportJobListResponse,
  ImportJobRun,
  ImportJobRunListResponse,
} from '../types/importJobs';

// `config.API_URL` resolves to `/api` (or similar), and the admin REST
// surface lives under `/v1/admin/...` — same prefix the existing
// scheduled-jobs hook uses.
const BASE_PATH = '/v1/admin/import-jobs';

export const importJobsKeys = {
  all: ['admin', 'import-jobs'] as const,
  lists: () => [...importJobsKeys.all, 'list'] as const,
  detail: (id: string) => [...importJobsKeys.all, 'detail', id] as const,
  runsList: (id: string, limit: number, offset: number) =>
    [...importJobsKeys.all, 'runs', id, limit, offset] as const,
  run: (jobId: string, runId: string) =>
    [...importJobsKeys.all, 'run', jobId, runId] as const,
};

/**
 * List all import jobs the current admin can see (scoped to their org by
 * the JWT). Polls every 10s when any job has a recent in-flight run so
 * status/last_run_at stay fresh while the user watches the page.
 */
export const useImportJobsAdmin = () => {
  return useQuery<ImportJobListResponse>({
    queryKey: importJobsKeys.lists(),
    queryFn: async () =>
      (await fetchWithAuth(BASE_PATH)) as ImportJobListResponse,
    refetchInterval: false,
  });
};

/**
 * Single import job detail.
 */
export const useImportJobAdmin = (id: string | null | undefined) => {
  return useQuery<ImportJob>({
    queryKey: id ? importJobsKeys.detail(id) : (['admin', 'import-jobs', 'detail', 'nil'] as QueryKey),
    queryFn: async () =>
      (await fetchWithAuth(`${BASE_PATH}/${id}`)) as ImportJob,
    enabled: Boolean(id),
  });
};

/**
 * Paginated run history for a job. Refetches every 5s while any run is
 * pending/running so the user sees the in-flight tick advance without
 * a manual refresh.
 */
export const useImportJobRunsAdmin = (
  jobId: string | null | undefined,
  limit: number = 25,
  offset: number = 0,
) => {
  return useQuery<ImportJobRunListResponse>({
    queryKey: jobId
      ? importJobsKeys.runsList(jobId, limit, offset)
      : (['admin', 'import-jobs', 'runs', 'nil'] as QueryKey),
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      return (await fetchWithAuth(
        `${BASE_PATH}/${jobId}/runs?${params.toString()}`,
      )) as ImportJobRunListResponse;
    },
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data as ImportJobRunListResponse | undefined;
      // Three polling cases:
      //   1. List has a pending/running row → keep polling so the in-flight
      //      tick advances in the UI without a manual refresh.
      //   2. List is empty (newly-created monitor, first tick hasn't fired
      //      yet) → keep polling on a slower cadence so the first run
      //      appears as soon as it's created. Without this the panel sits
      //      on "No runs yet" forever even after the tick lands, because
      //      no row ever existed in the cache to trigger refetch via case 1.
      //   3. List is fully terminal → stop polling.
      if (!data) return 5000;
      const hasRunning = data.items?.some(
        (r) => r.status === 'running' || r.status === 'pending',
      );
      if (hasRunning) return 5000;
      if ((data.items?.length ?? 0) === 0) return 10000;
      return false;
    },
  });
};

const invalidateImportJobLists = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: importJobsKeys.lists() });

const invalidateImportJobDetail = (queryClient: QueryClient, jobId: string) =>
  queryClient.invalidateQueries({ queryKey: importJobsKeys.detail(jobId) });

/**
 * Pause a monitor. Backend sets status=paused + next_run_at=null so the
 * import tick stops enqueueing it. Invalidates the list + detail so the row
 * badge flips without a manual refresh.
 */
export const usePauseImportJob = () => {
  const queryClient = useQueryClient();
  return useMutation<ImportJob, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}/pause`, {
        method: 'POST',
        body: JSON.stringify({}),
      })) as ImportJob,
    onSuccess: (_data, id) => {
      invalidateImportJobLists(queryClient);
      invalidateImportJobDetail(queryClient, id);
    },
  });
};

/**
 * Resume a paused monitor. Backend recomputes next_run_at from the cadence
 * so the tick picks it up again.
 */
export const useResumeImportJob = () => {
  const queryClient = useQueryClient();
  return useMutation<ImportJob, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}/resume`, {
        method: 'POST',
        body: JSON.stringify({}),
      })) as ImportJob,
    onSuccess: (_data, id) => {
      invalidateImportJobLists(queryClient);
      invalidateImportJobDetail(queryClient, id);
    },
  });
};

/**
 * Delete a monitor and its run history. The backend returns 204 (fetchWithAuth
 * yields null) and cascades the run rows at the DB level.
 */
export const useDeleteImportJob = () => {
  const queryClient = useQueryClient();
  return useMutation<null, Error, string>({
    mutationFn: async (id) =>
      (await fetchWithAuth(`${BASE_PATH}/${id}`, {
        method: 'DELETE',
      })) as null,
    onSuccess: () => invalidateImportJobLists(queryClient),
  });
};

/**
 * Full detail for a single run — surfaces the persisted state blob
 * (records_imported, fetched_count, cursor_after, source_record_type, …)
 * + the error string. The state shape is source-dependent, so the page
 * renders it as JSON until a per-source typed view is built.
 */
export const useImportJobRunAdmin = (
  jobId: string | null | undefined,
  runId: string | null | undefined,
) => {
  return useQuery<ImportJobRun>({
    queryKey:
      jobId && runId
        ? importJobsKeys.run(jobId, runId)
        : (['admin', 'import-jobs', 'run', 'nil'] as QueryKey),
    queryFn: async () =>
      (await fetchWithAuth(
        `${BASE_PATH}/${jobId}/runs/${runId}`,
      )) as ImportJobRun,
    enabled: Boolean(jobId && runId),
    refetchInterval: (query) => {
      const data = query.state.data as ImportJobRun | undefined;
      const isRunning = data?.status === 'running' || data?.status === 'pending';
      return isRunning ? 2000 : false;
    },
  });
};
